import { Check, ChevronDown, Flame, FlaskConical } from 'lucide-react';
import { Fragment } from 'react';

import {
    type AgentTestMode,
    type RawToolDescriptionMode,
} from '@apps/web/components/phoebe_chat/agent_test_mode';
import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { Switch } from '@apps/web/components/ui/switch';
import {
    V3_AGENT_MODEL_OPTIONS,
    type V3AgentModelOption,
    type V3AgentModelProvider,
} from '@apps/web/features/phoebe_chat_v3/model/model_options';
import { cn } from '@apps/web/lib/utils';

function resolveOption(model: string): V3AgentModelOption {
    const listedOption = V3_AGENT_MODEL_OPTIONS.find(
        (option) => option.id === model,
    );
    if (listedOption !== undefined) {
        return listedOption;
    }
    const provider: V3AgentModelProvider = model.startsWith('gpt-')
        ? 'openai'
        : model.startsWith('accounts/fireworks/')
          ? 'fireworks'
          : 'anthropic';
    return { id: model, label: model, provider };
}

function V3ModelIcon({
    provider,
    className,
}: {
    readonly provider: V3AgentModelProvider;
    readonly className?: string;
}) {
    if (provider === 'fireworks') {
        return (
            <Flame
                aria-hidden={true}
                className={cn('size-3.5 shrink-0', className)}
            />
        );
    }
    return (
        <img
            src={provider === 'anthropic' ? '/anthropic.svg' : '/openai.svg'}
            alt=""
            aria-hidden={true}
            className={cn('size-3.5 shrink-0 object-contain', className)}
        />
    );
}

export function V3AgentModelPicker({
    pinnedModel,
    resolvedModel,
    disabled = false,
    onModelChange,
}: {
    readonly pinnedModel: string | null | undefined;
    readonly resolvedModel: string | null | undefined;
    readonly disabled?: boolean;
    readonly onModelChange: (model: string | null) => void;
}) {
    const displayedModel = resolvedModel ?? pinnedModel ?? null;
    const displayedOption =
        displayedModel === null ? null : resolveOption(displayedModel);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    className="bg-muted/70 hover:bg-muted h-7 max-w-full gap-1.5 rounded-full px-2.5 text-xs font-medium"
                >
                    {displayedOption === null ? null : (
                        <V3ModelIcon
                            provider={displayedOption.provider}
                            className="text-muted-foreground shrink-0"
                        />
                    )}
                    <span className="truncate">
                        {displayedOption?.label ?? 'Default'}
                    </span>
                    <ChevronDown className="text-muted-foreground size-3 shrink-0" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 p-2">
                <div className="text-muted-foreground px-2 py-2 text-sm font-medium">
                    Select a model
                </div>
                <DropdownMenuItem
                    disabled={disabled}
                    onClick={disabled ? undefined : () => onModelChange(null)}
                >
                    <span className="size-3.5 shrink-0" />
                    <span className="flex-1">Default</span>
                    {(pinnedModel ?? null) === null ? (
                        <Check className="size-4" />
                    ) : null}
                </DropdownMenuItem>
                {V3_AGENT_MODEL_OPTIONS.map((option) => (
                    <DropdownMenuItem
                        key={option.id}
                        disabled={disabled}
                        onClick={
                            disabled
                                ? undefined
                                : () => onModelChange(option.id)
                        }
                    >
                        <V3ModelIcon provider={option.provider} />
                        <span className="flex-1">{option.label}</span>
                        {pinnedModel === option.id ? (
                            <Check className="size-4" />
                        ) : null}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

const TEST_MODE_OPTIONS: ReadonlyArray<{
    readonly id: AgentTestMode;
    readonly label: string;
    readonly description: string;
}> = [
    {
        id: 'normal',
        label: 'Normal',
        description: 'Use the full Phoebe prompt, skills, and tools.',
    },
    {
        id: 'raw',
        label: 'Raw',
        description:
            'Use every tool with no system prompt or skill guidance. Tool detail is configured below.',
    },
    {
        id: 'parallel',
        label: 'Parallel',
        description: 'Run Normal and Raw together for comparison.',
    },
];

export function V3AgentTestModePicker({
    mode,
    rawToolDescriptions = 'stripped',
    allowParallel = false,
    disabled = false,
    onModeChange,
    onRawToolDescriptionsChange,
}: {
    readonly mode: AgentTestMode;
    readonly rawToolDescriptions?: RawToolDescriptionMode;
    readonly allowParallel?: boolean;
    readonly disabled?: boolean;
    readonly onModeChange: (mode: AgentTestMode) => void;
    readonly onRawToolDescriptionsChange?: (
        mode: RawToolDescriptionMode,
    ) => void;
}) {
    const selected = TEST_MODE_OPTIONS.find((option) => option.id === mode);
    const availableOptions = allowParallel
        ? TEST_MODE_OPTIONS
        : TEST_MODE_OPTIONS.filter((option) => option.id !== 'parallel');

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    aria-label="Agent test mode"
                    title="Admin testing mode"
                    className="bg-muted/70 hover:bg-muted h-7 max-w-full gap-1.5 rounded-full px-2.5 text-xs font-medium"
                >
                    <FlaskConical className="text-muted-foreground size-3.5 shrink-0" />
                    <span className="truncate">{selected?.label ?? mode}</span>
                    <ChevronDown className="text-muted-foreground size-3 shrink-0" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-80 p-2">
                <div className="px-2 py-2">
                    <div className="text-sm font-medium">Testing mode</div>
                    <div className="text-muted-foreground mt-0.5 text-xs">
                        Admin testing only. Choosing another mode starts a new
                        test thread.
                    </div>
                </div>
                {availableOptions.map((option) => (
                    <DropdownMenuItem
                        key={option.id}
                        disabled={disabled}
                        onClick={
                            disabled ? undefined : () => onModeChange(option.id)
                        }
                        className="items-start gap-2"
                    >
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm">
                                {option.label}
                            </span>
                            <span className="text-muted-foreground block text-xs whitespace-normal">
                                {option.description}
                            </span>
                        </span>
                        {mode === option.id ? (
                            <Check className="mt-0.5 size-4 shrink-0" />
                        ) : null}
                    </DropdownMenuItem>
                ))}
                {mode === 'raw' || mode === 'parallel' ? (
                    <Fragment>
                        <DropdownMenuSeparator />
                        <div className="flex items-center gap-3 px-2 py-2">
                            <label
                                htmlFor="raw-tool-descriptions"
                                className="min-w-0 flex-1"
                            >
                                <span className="block text-sm font-medium">
                                    Full tool descriptions
                                </span>
                                <span className="text-muted-foreground block text-xs">
                                    Off sends one line per tool and no parameter
                                    guidance.
                                </span>
                            </label>
                            <Switch
                                id="raw-tool-descriptions"
                                aria-label="Full tool descriptions"
                                checked={rawToolDescriptions === 'full'}
                                disabled={
                                    disabled ||
                                    onRawToolDescriptionsChange === undefined
                                }
                                onCheckedChange={(checked) =>
                                    onRawToolDescriptionsChange?.(
                                        checked ? 'full' : 'stripped',
                                    )
                                }
                            />
                        </div>
                    </Fragment>
                ) : null}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
